import { YearsService } from '../../../../@core/utils/years.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { NbDialogService, NbCardModule, NbAlertModule, NbButtonModule } from '@nebular/theme';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import { combineLatest, of, Subscription } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { I18nService } from '../../../../@core/utils/i18n.service';
import * as R from 'ramda';
import { KPI } from '../../../../@core/api';
import {
  ChartFiltersService,
  ChartsService,
  InitiativesService,
  InvestmentScenariiService,
  PerimeterEnergyTrajectoryService,
  PerimeterPathwayService,
  PerimetersService,
  ScopeService,
  SpreadsheetColumnsInitiativesService,
  SpreadsheetColumnsInvestmentsService,
  UtilsService,
} from '../../../../@core/utils';
import { idMapToArray } from '../../../../@core/utils/scope.service';
import { LayoutService } from '../../../../@core/utils';
import { INITIATIVE_FILTER_KEY_PREFIX } from '../../../../@core/utils/constants';
import { ExcelExplanationsDialogComponent, SheetDialogComponent } from '../../../../@theme/components';
import { EmissionFactorService } from '../../../../@core/utils/emission-factor.service';
import { HighchartsChartModule } from 'highcharts-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgClass } from '@angular/common';
import { ConsumptionTypeButtonComponent } from '../../consumption-type-button/consumption-type-button.component';
import { EnergySettingsDialogComponent } from '../../energy-settings-dialog/energy-settings-dialog.component';
import { BaseTrajectoryChartComponent } from '../base-trajectory-chart/base-trajectory-chart.component';

@Component({
  selector: 'app-carbon-trajectory-chart',
  styleUrls: ['../base-trajectory-chart/base-trajectory-chart.component.scss'],
  templateUrl: '../base-trajectory-chart/base-trajectory-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NbCardModule,
    NgClass,
    
    FontAwesomeModule,
    HighchartsChartModule,
    NbAlertModule,
    NbButtonModule,
    TranslateModule,
    ConsumptionTypeButtonComponent,
  ],
})
export class CarbonTrajectoryChartComponent
  extends BaseTrajectoryChartComponent
  implements OnInit, OnChanges, OnDestroy {

  @Input() public sitesNumber: number = null;
  @Input() public isSideView: boolean = false;
  @Input() public useMainScenario: boolean = false;
  @Input() public perimeters: KPI.Perimeter[] = [];
  @Input() public forcedScenarii: KPI.InvestmentScenario[];
  @Input() public selectedBuildingIds: number[] = [];
  @Input() public isSiteObjectiveEligible: boolean = true;

  private scenarii: KPI.InvestmentScenario[];
  private budgetOrRatioFilter: KPI.BudgetOrRatioCarbonFilter;
  private selectedPerimeterIds: number[] = [];
  private subscriptions: Subscription[] = [];
  private chartTitle: string;
  public chosenProvider: string;
  private pathwayStartYear = new Date().getFullYear() - 3;
  private yearsRange: number[] = R.range(this.pathwayStartYear, 2051);
  public providerId: number;
  public selectedToUPathway: KPI.SelectedToUPathway | null = null;

  constructor(
    protected dialogService: NbDialogService,
    protected layoutService: LayoutService,
    protected router: Router,
    protected pathwayService: PerimeterPathwayService,
    private cdRef: ChangeDetectorRef,
    private i18nService: I18nService,
    private yearsService: YearsService,
    private utilsService: UtilsService,
    private translate: TranslateService,
    private chartsService: ChartsService,
    private perimetersService: PerimetersService,
    private perimeterEnergyTrajectoryService: PerimeterEnergyTrajectoryService,
    private initiativesService: InitiativesService,
    private chartFiltersService: ChartFiltersService,
    private emissionFactorService: EmissionFactorService,
    private investmentScenariiService: InvestmentScenariiService,
    private scopeService: ScopeService,
    private spreadsheetColumnsInitiativesService: SpreadsheetColumnsInitiativesService,
    private spreadsheetColumnsInvestmentsService: SpreadsheetColumnsInvestmentsService,
  ) {
    super(dialogService, layoutService, router, pathwayService);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.noConsumptionSentence = this.translate.instant("No consumption for the perimeter (carbon)");
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSiteObjectiveEligible'] && !changes['isSiteObjectiveEligible'].currentValue && this.providerId === -1) {
      this.emissionFactorService.setSelectedProvider('carbon', 0);
    }
    this.update();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  protected update(): void {
    this.subscriptions.push(
      combineLatest([
        this.yearsService.latestYears$,
        this.perimetersService.perimeters$,
        this.investmentScenariiService.selectedScenarii$.pipe(
          map((scenarii) => scenarii.filter((s) => !!s)),
        ),
        this.investmentScenariiService.mainScenario$,
      ]).pipe(
        switchMap(([[start_year, end_year], perimeters, selectedScenarii, mainScenario]) => {
          this.scenarii = this.useMainScenario
            ? [mainScenario]
            : this.forcedScenarii
              ? this.forcedScenarii
              : selectedScenarii;
          if (!this.perimeters.length) {
            this.perimeters = perimeters;
          }

          const buildingIds = this.selectedBuildingIds?.length
            ? this.selectedBuildingIds
            : PerimetersService.getBuildingsIds(this.perimeters);
          const energyData$ = combineLatest(
            this.scenarii.map((scenario) => {
              const options = {
                buildings: buildingIds.join(','),
                scenario: scenario.id,
              };
              return this.chartsService.getEnergyTrajectoryData(options).pipe(
                map((energyData) => ({ scenario, trajectoryPoints: energyData.trajectory_points }))
              );
            }),
          );
          return combineLatest([
            combineLatest(
              this.scenarii.map((scenario) =>
                this.chartsService.getCarbonTrajectoryData({
                  buildings: buildingIds,
                  scenario: scenario.id,
                  start_year,
                  end_year,
                }),
              ),
            ),
            of([start_year, end_year]),
            this.chartFiltersService.filter$('budgetOrRatioCarbon'),
            this.emissionFactorService.getSelectedProvider$('carbon'),
            this.pathwayService.requestPathway$(this.perimeters, this.yearsRange),
            this.initiativesService.initiativeEnergyTypes$,
            this.pathwayService.getSelectedPathway(),
            energyData$,
            this.scopeService.investmentStatuses$.pipe(idMapToArray()),
            this.perimeterEnergyTrajectoryService.selectedTrajectoryPerimeterIds$,
          ]);
        }),
      ).subscribe(([chartData, [startYear, endYear], budgetOrRatioFilter, provider, pathway, energyTypes, selectedToUPathway, trajectories, investmentStatuses, selectedPerimeterIds]) => {
        this.selectedPerimeterIds = selectedPerimeterIds;
        this.chosenProvider = provider.name;
        this.providerId = provider.id
        this.budgetOrRatioFilter = <number>budgetOrRatioFilter;
        this.chartTitle = this.translate.instant('Carbon trajectory')
        this.selectedToUPathway = selectedToUPathway;
        const years = R.range(startYear, endYear + 1);
        const i18n = this.i18nService;

        const trajectoryPoints = trajectories[0].trajectoryPoints;
        const relevantInvestmentStatuses = investmentStatuses.filter((status) =>
          (status.hypothesis || trajectoryPoints.some((point) => point.investments_by_year_and_status.some((s) => s.id === status.id)))
          && status.show_in_charts,
        ).sort((a, _b) => a.hypothesis ? 1 : -1);

        const baseColumnSerie = {
          budget: (
            budgetOrRatioFilter === KPI.BudgetOrRatioCarbonFilter.CAPEX_AMOUNT
          ),
          column: true,
          type: 'column',
          yAxis: 'y-axis-2',
          zIndex: 1,
          opacity: 0.9,
          dataLabels: {
            enabled: this.scenarii?.length > 1,
            formatter: function () {
              if (this.y) {
                return i18n.formatCurrency(this.y);
              }
            }
          },
        };

        const objectiveSerieTemplate = {
          dashStyle: 'dash',
          lineWidth: 1.5,
          marker: {
            enabled: false,
          },
          dataLabels: {
            enabled: false
          },
          column: false,
          trajectory: false,
          type: 'line',
          yAxis: 'y-axis-1',
          zIndex: 3,
        };

        // We need at least 1 energy declaration per selected site or else we don't show the chart
        // Skip this check when specific buildings are selected, as sites_count is per-site not per-building
        const allSitesHaveTrajectories = this.selectedBuildingIds?.length > 0
          || trajectories[0].trajectoryPoints[0].sites_count === this.perimeters.length;

        if (selectedToUPathway === null || !allSitesHaveTrajectories) {
          this.displayNoData = true;
          this.loading = false;
          setTimeout(() => {
            this.reflow();
            this.cdRef.detectChanges();
          }, 50);
        } else {

          const objectiveData = [2030, 2040, 2050].map((year) => {
            if (this.providerId === null || this.providerId === 0) {
              return [];
            }
            if (this.providerId !== -1 && selectedToUPathway?.emission) {
              return this.getObjectiveDataByToU(years.length, selectedToUPathway.emission[year]);
            } else if (pathway?.site_objectives) {
              return this.getObjectiveDataBySite(years.length, pathway.site_objectives, year, 'ghg', this.selectedPerimeterIds);
            } else {
              return [];
            }
          });

          const [objectiveData2030, objectiveData2040, objectiveData2050] = objectiveData;

          const series = [
            // Scenarii carbon trajectories
            ...this.scenarii.map((scenario, i) => {
              return {
                name: this.translate.instant('Trajectory') + ' ' + scenario.name,
                data: years.map((year) =>
                  chartData[i][year]?.carbonTrajectory?.emission
                    ? chartData[i][year].carbonTrajectory.emission
                    : null,
                ),
                lineWidth: 1,
                marker: {
                  symbol: 'square',
                  radius: 5
                },
                type: 'line',
                yAxis: 'y-axis-1',
                zIndex: 2,
                trajectory: true,
                color: scenario.color,
                scenario,
                energyTypes,
                contribution: years.reduce((acc, year) => ({
                  ...acc,
                  [year]: chartData[i][year]?.carbonTrajectory?.contribution
                    ? chartData[i][year].carbonTrajectory.contribution
                    : null,
                }), {}),
              };
            }),
            // Planned investments by scenario (multi-scenario view)
            ...this.scenarii.map((scenario, i) => ({
              ...baseColumnSerie,
              name: this.translate.instant('Planned invest') + ' ' + this.scenarii[i].name,
              data: years.map((year) => {
                switch (budgetOrRatioFilter) {
                  case KPI.BudgetOrRatioCarbonFilter.TRAJECTORY_ONLY:
                    return null;
                  default:
                    return chartData[i][year].budget
                      ? chartData[i][year].budget.investment_budget
                      : null;
                }
              }),
              color: scenario.color,
              scenario,
              visible: this.scenarii.length > 1 && budgetOrRatioFilter !== KPI.BudgetOrRatioCarbonFilter.TRAJECTORY_ONLY,
              showInLegend: this.scenarii.length > 1 && budgetOrRatioFilter !== KPI.BudgetOrRatioCarbonFilter.TRAJECTORY_ONLY,
            })),
            // Splitting investments by status - single scenario
            ...relevantInvestmentStatuses.map((status, i) => ({
              name: !i ? this.translate.instant(_('invest_at_status'), { name: status.name }) : status.name,
              data: years.map((year) => {
                if (budgetOrRatioFilter === KPI.BudgetOrRatioCarbonFilter.TRAJECTORY_ONLY) {
                  return null;
                }
                const tp = trajectoryPoints.find((t) => t.year === year);
                return tp?.investments_by_year_and_status.find((s) => s.id === status.id)?.investment_price || 0;
              }),
              color: status.color,
              scenario: this.scenarii[0],
              investmentStatus: status.id,
              chartData: trajectoryPoints,
              altName: status.name,
              ...baseColumnSerie,
              visible: this.scenarii.length === 1 && budgetOrRatioFilter !== KPI.BudgetOrRatioCarbonFilter.TRAJECTORY_ONLY && !status.hypothesis,
              showInLegend: this.scenarii.length === 1 && budgetOrRatioFilter !== KPI.BudgetOrRatioCarbonFilter.TRAJECTORY_ONLY,
            })),
            // CARBON OBJECTIVE 2030
            {
              ...objectiveSerieTemplate,
              name: this.translate.instant('Objective')
                + ' 2030',
              data: objectiveData2030,
              lineWidth: 3,
              showInLegend: !!objectiveData2030[0],
              visible: !!objectiveData2030[0],

            },
            // CARBON OBJECTIVE 2040
            {
              ...objectiveSerieTemplate,
              name: this.translate.instant('Objective') + ' 2040',
              data: objectiveData2040,
              lineWidth: 3,
              showInLegend: !!objectiveData2040[0],
              visible: !!objectiveData2040[0],
            },
            // CARBON OBJECTIVE 2050
            {
              ...objectiveSerieTemplate,
              name: this.translate.instant('Objective') + ' 2050',
              data: objectiveData2050,
              lineWidth: 3,
              showInLegend: !!objectiveData2050[0],
              visible: !!objectiveData2050[0],
            },
          ];

          const categoryNames = Object.keys(chartData[0]);
          // Update chart options
          this.options = {
            ...this.getOptions(),
            // @ts-ignore
            xAxis: {
              ...this.getOptions().xAxis,
              // @ts-ignore
              categories: categoryNames,
              tickWidth: 0,
              labels: {
                style: {
                  fontSize: '13px',
                },
              },
            },
            // @ts-ignore
            series: series as Highcharts.SeriesColumnOptions[],
          };
          this.loading = false;
          setTimeout(() => {
            this.reflow();
            this.cdRef.detectChanges();
          }, 50);
        }
      }),
    );
  }

  protected getOptions(): Highcharts.Options {
    const utilsService = this.utilsService;
    const translate = this.translate;
    const i18n = this.i18nService;

    return {
      ...super.getOptions(),
      colors: [
        '#4472C4',
        '#5BA7A9',
        '#C5E0B4',
        '#00b0f0',
        '#ffc000',
        '#ff9b9b',
        '#7030A0',
        '#ADC1E5',
        '#ADC1E5'
      ],
      title: {
        style: super.getOptions().title.style,
        align: 'left',
        text: this.chartTitle,
      },
      legend: {
        align: 'center',
        verticalAlign: 'top',
        itemHiddenStyle: super.getOptions().legend.itemHiddenStyle,
        itemStyle: {
          fontSize: '0.9375rem',
          fontWeight: 'normal',
        },
        symbolHeight: 11,
        symbolRadius: 0,
        itemMarginBottom: 8,
      },
      tooltip: {
        enabled: true,
        split: true,
        shared: true,
        useHTML: true,
        formatter: function () {
          let tooltipText: string[] = [''].concat(
            this.points
              ? this.points.map((point) => {
                if (point.series.userOptions['trajectory']) {
                  const year = point.point.category['name'];
                  const contribution = point.series.userOptions['contribution'];
                  const energyTypes: KPI.InitiativeEnergyType[] = point.series.userOptions['energyTypes'];
                  const refrigerant_emission: number = Math.max(0, contribution?.[year]?.direct);
                  const energy_emission_map: { [energyTypeId: number]: number } = contribution?.[year]?.indirect;

                  const emissionTotal = refrigerant_emission
                    + Object.values(energy_emission_map).reduce((acc, emission) => acc + emission, 0);

                  let emissionsPercentStr = Object.keys(energy_emission_map).filter((key) =>
                    energy_emission_map[key],
                  ).map((key) => {
                    const energyType = energyTypes.find((energyType) => energyType.id === Number(key));
                    const energyPercent = Math.min(
                      100,
                      Math.round(energy_emission_map[key] / emissionTotal * 100)
                    );
                    return `<b>${energyType.name}</b> ${energyPercent > 0 ? energyPercent : 0}%`;
                  }).join(' <b>|</b> ');

                  if (refrigerant_emission > 0) {
                    const refrigerantPercent = Math.round(refrigerant_emission / emissionTotal * 100);
                    if (refrigerantPercent) {
                      emissionsPercentStr += ` <b> | ${translate.instant(_('Refrigerant fluids'))}</b> ${refrigerantPercent}%`;
                    }
                  }

                  return `<b> ${point.series.name}: </b>`
                    + utilsService.round2dec(point.y, 1)
                    + ' '
                    + translate.instant(_('kg_eq_CO2_m2_year'))
                    + `<br/><span style="color: #c71f1f;">${emissionsPercentStr}</span>`;
                } else if (point.series.userOptions["column"]) {
                  const investmentStatus = point.series.userOptions['investmentStatus'];
                  const altName = point.series.userOptions['altName'];
                  const chartDataPoints = point.series.userOptions['chartData'];
                  let text = '<b> ' + point.series.name + ': </b>';
                  if (investmentStatus && chartDataPoints) {
                    const year = Number(point.point.category);
                    const nbInv = chartDataPoints.find((tp) =>
                      tp.year === year
                      && tp.investments_by_year_and_status.some((s) => s.id === investmentStatus)
                    )?.investments_by_year_and_status.find((s) => s.id === investmentStatus)?.number_of_investment || 0;
                    return translate.instant(
                      _('{nbInv} invest on status « {status} » for {budget}'),
                      { nbInv, status: altName, budget: i18n.formatCurrency(point.y) }
                    );
                  }
                  return text + i18n.formatCurrency(point.y);
                } else {
                  return '';
                }
              })
              : []
          );
          return tooltipText;
        },
      },
      plotOptions: {
        column: {
          stacking: this.scenarii?.length === 1 ? 'normal' : undefined,
        },
        spline: {
          dataLabels: {
            enabled: false,
          },
        },
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: this.getColumnClickHandler(),
            },
          },
        }
      },
      yAxis: [{
        id: 'y-axis-1',
        gridLineWidth: 1,
        minorGridLineWidth: 0,
        min: 0,
        labels: {
          useHTML: true,
          style: {
            fontSize: '12px',
            fontWeight: 'normal',
          },
          enabled: true,
          formatter: function () {
            return utilsService.formateNumber(this.value) + ' ' + translate.instant(_('kg_eq_CO2_m2_year'));
          },
        },
        title: {
          text: null,
        },
      },
      {
        id: 'y-axis-2',
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 'normal',
          },
          enabled: false,
        },
        title: {
          text: null,
        },
        stackLabels: {
          style: {
            fontWeight: 'normal',
          },
          enabled: this.scenarii?.length === 1 && this.budgetOrRatioFilter !== KPI.BudgetOrRatioCarbonFilter.TRAJECTORY_ONLY,
          formatter: function () {
            if (this.total) {
              return i18n.formatCurrency(this.total);
            }
          },
        },
        plotLines: [{
          color: '#9d9d9d',
          width: 1,
          value: 0,
          zIndex: 999,
        }]
      }],
      xAxis: {
        crosshair: true,
        labels: {
          style: {
            fontSize: '12px',
            color: '#091902',
          },
        },
      },
      series: [],
    };
  }

  protected getChartOptions(): Highcharts.ChartOptions {
    return {
      ...super.getChartOptions(),
      type: 'spline',
      alignTicks: false,
    };
  }

  public excelMessageHandler() {
    this.dialogService.open(ExcelExplanationsDialogComponent);
  }

  private getColumnClickHandler(): (
    event: Highcharts.SeriesClickEventObject,
  ) => void {

    const router = this.router;
    const isSideView = this.isSideView;
    const perimeters = this.perimeters;
    const dialogService = this.dialogService;
    const investColumnsService = this.spreadsheetColumnsInvestmentsService;
    const initiativeColumnsService = this.spreadsheetColumnsInitiativesService;

    return function (event: Highcharts.SeriesClickEventObject) {

      const year = event.point.category['name'];
      const scenario = event.point.series.userOptions['scenario'];
      const isBudgetColumnClicked: boolean = event.point.series.userOptions['budget'];

      if (isBudgetColumnClicked) {

        investColumnsService.plannedStatusFilter$.pipe(first()).subscribe((statuses) => {

          const filters: any = {
            [`year_scenario_${scenario.id}`]: [year],
            [`has_initiatives`]: [true],
            [`status_scenario_${scenario.id}`]: statuses,
          };

          const monoPerimeters: string[] = [];
          const sites: number[] = [];

          if (perimeters) {
            perimeters.map((perimeter) => {
              if (perimeter?.parent) {
                // Mono perimeter case
                monoPerimeters.push(`${perimeter.parent.name} - ${perimeter.name} | ${perimeter.id}`);
              } else {
                // Site case
                sites.push(perimeter.id);
              }
            })
            if (!!monoPerimeters.length) {
              filters[`perimeter_id`] = monoPerimeters
            }
            if (!!sites.length) {
              filters[`site`] = sites
            }
          }

          dialogService.open(
            SheetDialogComponent,
            {
              autoFocus: false,
              context: {
                investmentsList: true,
                investmentsListScenario: scenario,
                investmentsListFilter: filters,
              },
            },
          );
        });

      } else {

        initiativeColumnsService.plannedStatusFilter$.pipe(first()).subscribe((statuses) => {

          if (isSideView) {
            const context: any = {
              autoFocus: false,
              initiativesList: true,
              initiativesListScenario: scenario,
              initiativesListFilters: {
                [`savings_start_year_scenario_${scenario.id}`]: [year],
                [`initiative_status_scenario_${scenario.id}`]: statuses.map(String)
              },
            };

            dialogService.open(
              SheetDialogComponent,
              {
                context,
              }
            );

          } else {

            const queryParams: any = {
              [`${INITIATIVE_FILTER_KEY_PREFIX}savings_start_year_scenario_${scenario}`]: year,
              [`${INITIATIVE_FILTER_KEY_PREFIX}initiative_status_scenario_${scenario}`]: statuses.join(','),
            };

            setTimeout(() => {
              const newRoute = ['energy'];
              router.navigate(newRoute, { queryParams });
            }, 300);
          }
        });
      }
    };
  }

  public openProviders(): void {
    this.dialogService.open(EnergySettingsDialogComponent, {
      autoFocus: false,
      context: {
        defaultSelectedTab: 'carbonObjectives',
      }
    });
  }
}
